import React, { useEffect, useState } from 'react';
import OrganizationLayout from '../../components/organization/OrganizationLayout';

// Services
import { Member, fetchOrganizationMembers, refreshAccessCode } from '../../services/organization/members';

// Shadcn Components
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Skeleton } from "../../components/ui/skeleton";
import { Alert, AlertDescription } from "../../components/ui/alert";

// Icons
import { Search, Eye, EyeOff, RefreshCw, Users, Copy } from "lucide-react";

const getRoleDisplay = (role?: string) => {
    switch (role) {
        case 'super_admin':
            return 'Super Admin';
        case 'admin':
            return 'Admin';
        case 'user':
            return 'User';
        default:
            return role || 'Member';
    }
};

const getRoleVariant = (role?: string) => {
    switch (role) {
        case 'super_admin':
            return 'secondary';
        case 'admin':
            return 'default';
        default:
            return 'outline';
    }
};

const getRoleStyles = (role?: string) => {
    switch (role) {
        case 'super_admin':
            return 'bg-purple-100 text-purple-700';
        case 'admin':
            return 'bg-blue-100 text-blue-700';
        default:
            return 'bg-gray-100 text-gray-700';
    }
};

const OrganizationMembers: React.FC = () => {
    const [members, setMembers] = useState<Member[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchValue, setSearchValue] = useState<string>("");
    const [showAccessCode, setShowAccessCode] = useState<boolean>(true);
    const [accessCode, setAccessCode] = useState<string>("");
    const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        const loadMembers = async () => {
            try {
                setLoading(true);
                const data = await fetchOrganizationMembers();
                setMembers(data.members);
                setAccessCode(data.access_code);
            } catch (err) {
                console.error('Error fetching members:', err);
                setError(err instanceof Error ? err.message : 'Failed to fetch members');
            } finally {
                setLoading(false);
            }
        };

        loadMembers();
    }, []);

    const handleRefreshAccessCode = async () => {
        setIsRefreshing(true);
        setError(null);
        try {
            const data = await refreshAccessCode();
            setAccessCode(data.access_code);
        } catch (error: unknown) {
            console.error('Error refreshing access code:', error);
            setError(error instanceof Error ? error.message : 'An error occurred');
        } finally {
            setIsRefreshing(false);
        }
    };

    const handleCopyAccessCode = async () => {
        try {
            await navigator.clipboard.writeText(accessCode);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy access code:', err);
        }
    };

    const filteredMembers = searchValue === "" ? members : members.filter(member =>
        member.name?.toLowerCase().includes(searchValue.toLowerCase()) || member.email?.toLowerCase().includes(searchValue.toLowerCase())
    );

    if (loading) return (
        <OrganizationLayout>
            <div className="space-y-6">
                <div className="space-y-4">
                    <Skeleton className="h-8 w-[200px]" />
                    <div className="grid gap-4">
                        <Skeleton className="h-12 w-full" />
                        <Skeleton className="h-12 w-full" />
                        <Skeleton className="h-12 w-full" />
                    </div>
                </div>
            </div>
        </OrganizationLayout>
    );

    if (error) return (
        <OrganizationLayout>
            <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
            </Alert>
        </OrganizationLayout>
    );

    return (
        <OrganizationLayout>
            <div className="space-y-6 pb-8 lg:pb-12">
                <div className="flex items-center justify-between">
                    <h2 className="text-3xl font-bold tracking-tight">Members</h2>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Users className="h-4 w-4" />
                        <span>{members.length} {members.length === 1 ? 'Member' : 'Members'}</span>
                    </div>
                </div>
                
                <div className="flex flex-col lg:flex-row gap-4">
                    <div className="w-full lg:flex-1 lg:min-w-[200px]">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                type="text"
                                placeholder="Search members..."
                                value={searchValue}
                                onChange={(e) => setSearchValue(e.target.value)}
                                className="pl-9"
                            />
                        </div>
                    </div>

                    <div className="flex flex-col md:flex-row gap-4 w-full lg:w-auto">
                        <div className="w-full md:flex-1 md:min-w-[200px]">
                            <div className="relative">
                                <Input
                                    type={showAccessCode ? "password" : "text"}
                                    value={accessCode}
                                    readOnly
                                    placeholder="Organization Access Code"
                                    className="pr-20"
                                />
                                <div className="absolute right-0 top-0 h-full flex">
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-full w-10"
                                        onClick={() => setShowAccessCode(!showAccessCode)}
                                    >
                                        {showAccessCode ? (
                                            <EyeOff className="h-4 w-4" />
                                        ) : (
                                            <Eye className="h-4 w-4" />
                                        )}
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-full w-10"
                                        onClick={handleCopyAccessCode}
                                    >
                                        <Copy className={`h-4 w-4 ${copied ? 'text-green-500' : ''}`} />
                                    </Button>
                                </div>
                            </div>
                        </div>
                        <Button
                            onClick={handleRefreshAccessCode}
                            disabled={isRefreshing}
                            className="w-full md:w-auto whitespace-nowrap transition-transform hover:scale-105"
                        >
                            <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                            Refresh Code
                        </Button>
                    </div>
                </div>

                {filteredMembers.length > 0 ? (
                    <div className="grid gap-4">
                        {filteredMembers.map((member) => (
                            <Card key={member.user_id} className="overflow-hidden hover:shadow-md transition-shadow">
                                <CardContent className="p-6">
                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                        <div className="space-y-1 min-w-0 flex-1">
                                            <p className="font-medium truncate text-base">{member.name || member.user_id}</p>
                                            <p className="text-sm text-muted-foreground truncate">{member.email}</p>
                                        </div>
                                        <div className="flex-shrink-0">
                                            <Badge 
                                                variant={getRoleVariant(member.role)}
                                                className={getRoleStyles(member.role)}
                                            >
                                                {getRoleDisplay(member.role)}
                                            </Badge>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                ) : (
                    <Card>
                        <CardContent className="p-12 text-center text-muted-foreground">
                            <div className="flex flex-col items-center gap-3">
                                <Users className="h-10 w-10" />
                                <p className="text-lg font-medium">No members found</p>
                                <p className="text-sm">Try adjusting your search terms</p>
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>
        </OrganizationLayout>
    );
};

export default OrganizationMembers;